import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  InputAdornment,
  Snackbar,
  alpha,
  MenuItem,
  TablePagination,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import API from '../../api/axios';

const iconOptions = ['Flight', 'FlightTakeoff', 'Train', 'Hotel', 'Shield', 'Explore', 'TravelExplore'];

const ManageServices = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const defaultForm = { name: '', description: '', icon: 'TravelExplore', isActive: true };
  const [formData, setFormData] = useState(defaultForm);

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const { data } = await API.get('/services');
      // Map snake_case from backend to camelCase for frontend consistency
      const mapped = (Array.isArray(data) ? data : []).map((svc) => ({
        ...svc,
        isActive: Boolean(svc.is_active),
      }));
      setServices(mapped);
    } catch {
      setError('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (svc = null) => {
    if (svc) {
      setEditingService(svc);
      setFormData({ name: svc.name, description: svc.description, icon: svc.icon || 'TravelExplore', isActive: svc.isActive });
    } else {
      setEditingService(null);
      setFormData(defaultForm);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => { setOpenDialog(false); setEditingService(null); };
  const handleChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'isActive' ? checked : value }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (editingService) {
        await API.put(`/services/${editingService.id}`, formData);
        setSnackbar({ open: true, message: 'Service updated successfully', severity: 'success' });
      } else {
        await API.post('/services', formData);
        setSnackbar({ open: true, message: 'Service created successfully', severity: 'success' });
      }
      fetchServices();
      handleCloseDialog();
    } catch {
      setSnackbar({ open: true, message: 'Failed to save service', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (svc) => {
    try {
      await API.put(`/services/${svc.id}`, { isActive: !svc.isActive });
      setServices((prev) => prev.map((s) => (s.id === svc.id ? { ...s, isActive: !s.isActive } : s)));
      setSnackbar({ open: true, message: `Service ${!svc.isActive ? 'activated' : 'deactivated'}`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to update service status', severity: 'error' });
    }
  };

  const openDeleteDialog = (id) => { setDeleteId(id); setDeleteDialogOpen(true); };
  const closeDeleteDialog = () => { setDeleteId(null); setDeleteDialogOpen(false); };

  const handleDelete = async () => {
    try {
      await API.delete(`/services/${deleteId}`);
      setSnackbar({ open: true, message: 'Service deleted', severity: 'success' });
      fetchServices();
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete service', severity: 'error' });
    } finally {
      closeDeleteDialog();
    }
  };

  const filtered = services.filter((s) => !search || s.name?.toLowerCase().includes(search.toLowerCase()));
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Manage Services</Typography>
          <Typography variant="body2" color="text.secondary">Configure available services offered to customers</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2.5, background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)', color: '#1a1a1a', boxShadow: '0 4px 15px rgba(247,151,30,0.35)', '&:hover': { boxShadow: '0 8px 25px rgba(247,151,30,0.45)' } }}
        >
          Add New Service
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      <TextField
        size="small"
        placeholder="Search services..."
        value={search}
        onChange={(e) => { setSearch(e.target.value); setPage(0); }}
        sx={{ mb: 3, maxWidth: 400 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
      />

      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fc' }}>
                {['Service Name', 'Description', 'Icon', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary', py: 2, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.length === 0 ? (
                <TableRow><TableCell colSpan={5} sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>No services found.</TableCell></TableRow>
              ) : paginated.map((svc) => (
                <TableRow key={svc.id} sx={{ '&:hover': { bgcolor: alpha('#f7971e', 0.03) } }}>
                  <TableCell><Typography variant="body2" fontWeight={600}>{svc.name}</Typography></TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {svc.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={svc.icon} size="small" sx={{ fontWeight: 500, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1.5, fontSize: '0.75rem' }} />
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={svc.isActive}
                      onChange={() => handleToggleActive(svc)}
                      size="small"
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' },
                      }}
                    />
                    <Typography variant="caption" sx={{ ml: 0.5, color: svc.isActive ? '#10b981' : 'text.secondary', fontWeight: 600 }}>
                      {svc.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => handleOpenDialog(svc)} sx={{ color: '#667eea', '&:hover': { bgcolor: alpha('#667eea', 0.1) } }}><EditOutlinedIcon fontSize="small" /></IconButton>
                      <IconButton size="small" onClick={() => openDeleteDialog(svc.id)} sx={{ color: '#ef4444', '&:hover': { bgcolor: alpha('#ef4444', 0.1) } }}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField fullWidth label="Service Name" name="name" value={formData.name} onChange={handleChange} required />
            <TextField fullWidth multiline rows={4} label="Description" name="description" value={formData.description} onChange={handleChange} required />
            <TextField select fullWidth label="Icon" name="icon" value={formData.icon} onChange={handleChange}>
              {iconOptions.map((ic) => <MenuItem key={ic} value={ic}>{ic}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={<Switch checked={formData.isActive} onChange={handleChange} name="isActive" sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' } }} />}
              label={<Typography variant="body2" fontWeight={500}>Service Active</Typography>}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving} sx={{ borderRadius: 2, background: 'linear-gradient(135deg, #f7971e 0%, #ffd200 100%)', color: '#1a1a1a', boxShadow: 'none' }}>
            {saving ? <CircularProgress size={20} /> : (editingService ? 'Update Service' : 'Create Service')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} PaperProps={{ sx: { borderRadius: 3, maxWidth: 400 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Service</DialogTitle>
        <DialogContent><Typography variant="body2" color="text.secondary">Are you sure you want to permanently delete this service?</Typography></DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={closeDeleteDialog} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ borderRadius: 2, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' }, boxShadow: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((p) => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageServices;